import { Gateway } from '@/features/gateway/pages'
import { Interim } from '@/features/interim'
import { AdminLogin } from '@/features/login/admin'
import { ActivityLogin } from '@/features/login/user'
import { Logout } from '@/features/logout'
import { LoginPreview } from '@/features/systemSetting/pages/LoginPreview'
import {
  adminThemeColorAtom,
  reflectionStorage,
  schoolIdStorage,
  store,
} from '@/store'
import { Portal } from '@seiue/ui'
import { ThemeProvider } from '@seiue/ui/build/module/theme/Provider'
import { isNaN, uniqBy } from '@seiue/util'
import { ConfigProvider, Spin } from 'antd'
import { modalRoutes } from 'packages/route'
import {
  Agreement,
  ModalRoute,
  NotFound,
  Policy,
} from 'packages/route/components'
import { schoolApi } from 'packages/sdks-next/growp'
import { THEME_COLOR, getTheme } from 'packages/theme'
import { AnyType } from 'packages/types'
import { getUrlParams } from 'packages/utils'
import { FC, Suspense, useEffect } from 'react'
import { createGlobalState } from 'react-hooks-global-state'
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'
import styled, { ThemeProvider as StyledThemeProvider } from 'styled-components'

import { AdminLayout } from './Layout/AdminLayout'
import { SchoolContext } from './context'

const { useGlobalState } = createGlobalState({ themeColor: THEME_COLOR })
export { useGlobalState }

/**
 * 教师管理界面
 *
 * @returns page
 */
export const AdminHome: FC = () => {
  const reflection = store.get(reflectionStorage)
  const schoolId = store.get(schoolIdStorage)
  // 初始页面进入也许是在过渡页 interim，也许是在登录页
  const loginRegex = /\/login\/(.*)/
  const interimRegex = /\/interim\/(.*)/
  const interimResult = location.pathname.match(interimRegex)
  const loginResult = location.pathname.match(loginRegex)
  const sid = interimResult?.[1] || loginResult?.[1] || schoolId

  const isInterimPage = !!interimResult

  const _schoolId = getUrlParams('_school_id', location.search)
  const _refreshProxyToken = getUrlParams(
    '_refresh_proxy_token',
    location.search
  )

  const { data, loading } = schoolApi.useLoadSchool(
    {
      id: Number(sid) || 0,
    },
    { disable: isNaN(Number(sid)), omitAuthorizationHeader: true }
  )

  const isLoggedIn = !!reflection
  const [, setThemeColor] = useGlobalState('themeColor')

  useEffect(() => {
    store.set(adminThemeColorAtom, data?.themeColor || THEME_COLOR)
    const root: HTMLElement | null = document.querySelector(':root')
    if (root) {
      root.style.setProperty(
        '--ant-primary-color',
        data?.themeColor || THEME_COLOR
      )
    }
  }, [data?.themeColor])

  useEffect(() => {
    ConfigProvider.config({
      theme: {
        primaryColor: data?.themeColor || THEME_COLOR,
      },
    })

    setThemeColor(data?.themeColor || THEME_COLOR)
  }, [THEME_COLOR, ConfigProvider, data?.themeColor])

  const uniqueModalRoutes = uniqBy(modalRoutes, 'name')

  if (loading && !/personalization/.test(location.pathname))
    return <StyledSpin />
  return (
    <ThemeProvider themeColor={data?.themeColor || THEME_COLOR}>
      <StyledThemeProvider
        theme={getTheme(data?.themeColor || THEME_COLOR) as AnyType}
      >
        <SchoolContext.Provider
          value={{
            logo: data?.logo || '',
            bgImage: data?.bgImage || '',
            themeColor: data?.themeColor || '',
          }}
        >
          <Router>
            <Switch>
              <Route
                path="/serviceAgreement"
                component={Agreement}
              />
              <Route
                path="/policy"
                component={Policy}
              />
              <Route
                path="/404"
                component={NotFound}
              />
              <Route
                path="/interim/:schoolId"
                component={Interim}
              />
              <Route
                path="/login/:schoolId"
                component={AdminLogin}
              />
              <Route
                path="/logout"
                component={Logout}
              />
              <Route
                path="/user/gateway"
                component={Gateway}
              />
              <Route
                path="/login-preview"
                component={LoginPreview}
              />
              <Route
                path="/login-user-preview"
                component={ActivityLogin}
              />
              <Route
                render={() =>
                  isLoggedIn || (!!_schoolId && !!_refreshProxyToken) ? (
                    <Route component={AdminLayout} />
                  ) : !sid || isNaN(Number(sid)) ? (
                    <Redirect to={{ pathname: `/404` }} />
                  ) : isInterimPage ? (
                    <Redirect to={{ pathname: `/interim/${sid}` }} />
                  ) : (
                    <Redirect to={{ pathname: `/login/${sid}` }} />
                  )
                }
              />
            </Switch>
            {/* 全局注册 modalRoutes，使所有页面都能使用 modal 功能，之前在 AdminLayout 中注册了，但是因为 gateway 界面并没有被包裹在 AdminLayout 中，所以 modal 功能无法使用 */}
            <Suspense fallback={<StyledSpin />}>
              {uniqueModalRoutes.map((route) => (
                <ModalRoute
                  key={route.name}
                  route={route}
                />
              ))}
            </Suspense>
          </Router>
          <Portal.Host />
        </SchoolContext.Provider>
      </StyledThemeProvider>
    </ThemeProvider>
  )
}

const StyledSpin = styled(Spin)`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`
